import { ChevronLeft } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { BillingTermMetaResponseDto } from '../products/types/response';
import OfferCorrectionFormWrapper from './components/offer-correction-form/offer-correction-form.wrapper';
import OfferDetailWrapper from './components/offer-detail/offer-detail.wrapper';
import OfferFormWrapper from './components/offer-form/offer-form.wrapper';
import OfferListWrapper from './components/offer-list/offer-list.wrapper';
import { OfferDisplayStatus } from './constants/offer-display-status.enum';
import { OfferView } from './constants/offer-view.enum';
import { OFFERS_SEARCH_PARAM } from './constants/offers-page.constants';
import { isOfferView } from './lib/enum-guards';
import { getOfferDisplayStatus } from './lib/offer-status';
import { OfferListItemResponseDto } from './types/response';

export interface OffersPageProps {
  billingTerms: BillingTermMetaResponseDto[];
}

const OffersPage = ({ billingTerms }: OffersPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawView = searchParams.get(OFFERS_SEARCH_PARAM.view);
  const offerId = searchParams.get(OFFERS_SEARCH_PARAM.id);
  const requestedView = isOfferView(rawView) ? rawView : OfferView.LIST;
  // Edit, detail and correction need an offer – without one the list is shown.
  const view = requestedView !== OfferView.LIST && requestedView !== OfferView.CREATE && !offerId ? OfferView.LIST : requestedView;

  // Filter and page stay in the URL, so the list comes back exactly as it was left.
  const openView = (next: OfferView, id?: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === OfferView.LIST) params.delete(OFFERS_SEARCH_PARAM.view);
      else params.set(OFFERS_SEARCH_PARAM.view, next);
      if (id) params.set(OFFERS_SEARCH_PARAM.id, id);
      else params.delete(OFFERS_SEARCH_PARAM.id);
      return params;
    });
  };

  // The new offer is unsent and therefore open – show it on the first page, without the form in the history.
  const showCreatedOffer = () => setSearchParams(new URLSearchParams(), { replace: true });

  const openOffer = (offer: OfferListItemResponseDto) =>
    openView(getOfferDisplayStatus(offer) === OfferDisplayStatus.NOT_SENT ? OfferView.EDIT : OfferView.DETAIL, offer._id);

  const backToList = (
    <button
      type="button"
      onClick={() => openView(OfferView.LIST)}
      className="flex items-center gap-1 self-start text-[14px] text-[#888] hover:text-[#12282A]">
      <ChevronLeft className="h-4 w-4" />
      zur Übersicht
    </button>
  );

  if (view === OfferView.CREATE || view === OfferView.EDIT) {
    const isEdit = view === OfferView.EDIT;
    return (
      <>
        {backToList}
        <div>
          <h1 className="text-[32px] font-bold leading-[38px] text-[#12282A]">{isEdit ? 'Entwurf bearbeiten' : 'Neues Angebot'}</h1>
          <p className="text-[16px] text-[#888]">Rechts siehst du live, wie der Kunde den Checkout sieht.</p>
        </div>
        <OfferFormWrapper
          // A fresh form per offer – the form takes its values only once.
          key={offerId ?? 'new'}
          billingTerms={billingTerms}
          offerId={isEdit ? (offerId ?? undefined) : undefined}
          onSaved={isEdit ? () => openView(OfferView.LIST) : showCreatedOffer}
          onCancel={() => openView(OfferView.LIST)}
          onShowDetail={(id) => openView(OfferView.DETAIL, id)}
        />
      </>
    );
  }

  if (view === OfferView.DETAIL && offerId) {
    return (
      <>
        {backToList}
        <OfferDetailWrapper
          key={offerId}
          offerId={offerId}
          billingTerms={billingTerms}
          onBack={() => openView(OfferView.LIST)}
          onCorrect={() => openView(OfferView.CORRECT, offerId)}
        />
      </>
    );
  }

  if (view === OfferView.CORRECT && offerId) {
    return (
      <>
        {backToList}
        <div>
          <h1 className="text-[32px] font-bold leading-[38px] text-[#12282A]">Vertragsdaten korrigieren</h1>
          <p className="text-[16px] text-[#888]">
            Überschreibt Angebot, Unternehmen und Admin-Nutzer. Vor dem Speichern siehst du die Änderungsmail an den Kunden.
          </p>
        </div>
        <OfferCorrectionFormWrapper
          key={offerId}
          offerId={offerId}
          billingTerms={billingTerms}
          onDone={() => openView(OfferView.DETAIL, offerId)}
          onBack={() => openView(OfferView.LIST)}
        />
      </>
    );
  }

  return (
    <>
      <div>
        <h1 className="text-[32px] font-bold leading-[38px] text-[#12282A]">Angebote</h1>
        <p className="text-[16px] text-[#888]">Angebote erstellen, versenden und prüfen</p>
      </div>
      <OfferListWrapper billingTerms={billingTerms} onCreate={() => openView(OfferView.CREATE)} onOpen={openOffer} />
    </>
  );
};

export default OffersPage;
